<template>
  <div v-if="isOpen" class="modal-overlay is-open">
    <!-- Backdrop -->
    <div class="modal-backdrop is-open" @click="close" />

    <!-- Modal -->
    <div class="modal modal-xl is-open">
      <!-- Header -->
      <div class="modal-header">
        <h3 class="heading-4" style="color: var(--text-primary);">
          Create Support Ticket
        </h3>
        <button @click="close" class="btn btn-ghost btn-icon btn-sm">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Category Selection -->
        <div class="form-group">
          <label class="form-label">What type of request is this?</label>
          <div class="grid grid-cols-2 gap-3">
            <button
              v-for="cat in categories"
              :key="cat.value"
              type="button"
              @click="form.category = cat.value"
              class="p-4 rounded-lg border-2 text-left transition-all"
              :style="form.category === cat.value
                ? 'border-color: var(--color-primary-500, #3b82f6); background-color: var(--color-primary-50, #eff6ff);'
                : 'border-color: var(--border-default); background-color: var(--bg-surface);'"
              :class="{ 'hover:border-gray-300': form.category !== cat.value }">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  :style="form.category === cat.value
                    ? 'background-color: var(--color-primary-100, #dbeafe); color: var(--color-primary-600, #2563eb);'
                    : 'background-color: var(--bg-muted); color: var(--text-secondary);'">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div v-html="cat.icon" />
                </div>
                <div>
                  <p
                    class="text-body-sm font-medium"
                    :style="form.category === cat.value
                      ? 'color: var(--color-primary-700, #1d4ed8);'
                      : 'color: var(--text-primary);'">
                    {{ cat.label }}
                  </p>
                  <p class="text-caption" style="color: var(--text-tertiary);">
                    {{ cat.description }}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <!-- Subject -->
        <div class="form-group">
          <label class="form-label" for="subject">
            Subject <span class="text-red-500">*</span>
          </label>
          <input
            id="subject"
            v-model="form.subject"
            type="text"
            class="form-input"
            :class="{ 'border-red-500': touched.subject && !form.subject.trim() }"
            placeholder="Brief summary of your request"
            @blur="touched.subject = true">
          <p
            v-if="touched.subject && !form.subject.trim()"
            class="text-caption mt-1"
            style="color: var(--color-error, #ef4444);">
            Subject is required
          </p>
        </div>

        <!-- Description -->
        <div class="form-group">
          <label class="form-label" for="description">
            Description <span class="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            v-model="form.body"
            rows="10"
            class="form-textarea"
            :class="{ 'border-red-500': touched.body && !form.body.trim() }"
            placeholder="Provide details about your request or issue..."
            @blur="touched.body = true" />
          <p
            v-if="touched.body && !form.body.trim()"
            class="text-caption mt-1"
            style="color: var(--color-error, #ef4444);">
            Description is required
          </p>
        </div>

        <!-- File Attachments -->
        <div class="form-group">
          <label class="form-label">Attachments (optional)</label>
          <div
            class="border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer"
            style="border-color: var(--border-default);"
            @click="$refs.fileInput.click()"
            @dragover.prevent
            @drop.prevent="onDrop">
            <svg class="w-10 h-10 mx-auto mb-3" style="color: var(--text-muted);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p class="text-body-sm" style="color: var(--text-secondary);">
              <span style="color: var(--text-link);">Click to upload</span> or drag and drop
            </p>
            <p class="text-caption mt-1" style="color: var(--text-tertiary);">
              PNG, JPG, PDF up to 10MB each
            </p>
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              @change="onFileSelect">
          </div>

          <!-- Attached Files Preview -->
          <div v-if="form.attachments.length > 0" class="mt-3">
            <!-- Image Previews -->
            <div v-if="imageAttachments.length > 0" class="flex flex-wrap gap-3 mb-3">
              <div
                v-for="(file, index) in imageAttachments"
                :key="'img-' + index"
                class="relative group">
                <img
                  :src="getFilePreviewUrl(file)"
                  :alt="file.name"
                  class="w-20 h-20 object-cover rounded-lg border"
                  style="border-color: var(--border-default);">
                <button
                  type="button"
                  @click="removeAttachment(form.attachments.indexOf(file))"
                  class="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style="background-color: var(--color-error, #ef4444); color: white;">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <!-- Non-Image Files -->
            <div v-if="nonImageAttachments.length > 0" class="space-y-2">
              <div
                v-for="(file, index) in nonImageAttachments"
                :key="'file-' + index"
                class="flex items-center justify-between p-3 rounded-lg"
                style="background-color: var(--bg-muted);">
                <div class="flex items-center gap-3 min-w-0">
                  <svg class="w-5 h-5 flex-shrink-0" style="color: var(--text-tertiary);" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <div class="min-w-0">
                    <p class="text-body-sm truncate" style="color: var(--text-primary);">
                      {{ file.name }}
                    </p>
                    <p class="text-caption" style="color: var(--text-tertiary);">
                      {{ formatFileSize(file.size) }}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  @click="removeAttachment(form.attachments.indexOf(file))"
                  class="btn btn-ghost btn-icon btn-sm"
                  style="color: var(--text-tertiary);">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-error">
          <svg class="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{{ error }}</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <div class="flex items-center gap-4 w-full justify-between">
          <p
            v-if="!canSubmit && (touched.subject || touched.body)"
            class="text-caption"
            style="color: var(--color-error, #ef4444);">
            {{ validationHint }}
          </p>
          <span v-else />
          <div class="flex gap-3">
            <button type="button" @click="close" class="btn btn-secondary">
              Cancel
            </button>
            <button
              @click="submitTicket"
              :disabled="submitting"
              class="btn btn-primary">
              <svg v-if="submitting" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {{ submitting ? 'Creating...' : 'Create Ticket' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import { createSupportTicket } from '~/javascript/api/support_tickets.js'

// Props
const props = defineProps({
  websiteId: {
    type: String,
    required: true
  }
})

// Emits
const emit = defineEmits(['ticket-created'])

// Refs
const fileInput = ref(null)

// State
const isOpen = ref(false)
const submitting = ref(false)
const error = ref(null)

const form = reactive({
  category: 'content_change',
  subject: '',
  body: '',
  attachments: []
})

const touched = reactive({
  subject: false,
  body: false
})

const categories = [
  {
    value: 'content_change',
    label: 'Content Change',
    description: 'Update text, images, or info',
    icon: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>'
  },
  {
    value: 'bug_report',
    label: 'Bug Report',
    description: 'Something isn\'t working',
    icon: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>'
  },
  {
    value: 'question',
    label: 'Question',
    description: 'General questions or help',
    icon: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
  },
  {
    value: 'feature_request',
    label: 'Feature Request',
    description: 'Suggest new functionality',
    icon: '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>'
  }
]

// Computed
const canSubmit = computed(() => form.category && form.subject.trim().length > 0 && form.body.trim().length > 0)

const validationHint = computed(() => {
  const missing = []
  if (!form.subject.trim()) missing.push('subject')
  if (!form.body.trim()) missing.push('description')
  if (missing.length === 0) return ''
  return `Please fill in the ${missing.join(' and ')}`
})

const imageAttachments = computed(() => form.attachments.filter((f) => f.type.startsWith('image/')))

const nonImageAttachments = computed(() => form.attachments.filter((f) => !f.type.startsWith('image/')))

// Methods
const open = () => {
  isOpen.value = true
  resetForm()
  document.body.classList.add('overflow-hidden')
}

const close = () => {
  isOpen.value = false
  document.body.classList.remove('overflow-hidden')
}

const resetForm = () => {
  form.category = 'content_change'
  form.subject = ''
  form.body = ''
  form.attachments = []
  touched.subject = false
  touched.body = false
  error.value = null
}

const onFileSelect = (event) => {
  const files = Array.from(event.target.files)
  validateAndAddFiles(files)
  event.target.value = ''
}

const onDrop = (event) => {
  const files = Array.from(event.dataTransfer.files)
  validateAndAddFiles(files)
}

const validateAndAddFiles = (files) => {
  const validFiles = files.filter((file) => {
    if (file.size > 10 * 1024 * 1024) {
      error.value = `File "${file.name}" exceeds 10MB limit`
      return false
    }
    return true
  })
  form.attachments = [...form.attachments, ...validFiles]
}

const removeAttachment = (index) => {
  form.attachments.splice(index, 1)
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const getFilePreviewUrl = (file) => URL.createObjectURL(file)

const submitTicket = async() => {
  // Mark all fields as touched to show validation errors
  touched.subject = true
  touched.body = true

  if (!canSubmit.value || submitting.value) return

  try {
    submitting.value = true
    error.value = null

    const response = await createSupportTicket({
      websiteId: props.websiteId,
      category: form.category,
      subject: form.subject,
      body: form.body,
      attachments: form.attachments
    })

    if (response.data.success) {
      emit('ticket-created', response.data.ticket)
      close()
    } else {
      error.value = response.data.error || 'Failed to create ticket'
    }
  } catch (err) {
    console.error('Error creating ticket:', err) // eslint-disable-line no-console
    error.value = err.response?.data?.error || 'An error occurred while creating the ticket'
  } finally {
    submitting.value = false
  }
}

// Expose
defineExpose({ open, close })
</script>
